/**
 * Law tab — full-text search over the OFFLINE Massachusetts court-rules library.
 *
 * The verbatim rule sets (Rules of Criminal Procedure, Superior Court Rules,
 * SJC Rules & Orders, Appellate Procedure, Civil Procedure, and the
 * District/Municipal Courts Rules of Criminal Procedure) were downloaded from
 * the official mass.gov / Massachusetts Trial Court Law Libraries publications,
 * split into individual rules (scripts/parse-court-rules.ts), and shipped in
 * data/db/caselaw.sqlite (+ data/rules-text/*.txt). Everything runs on-device:
 * the SQLite engine is WASM and the DB + text files are service-worker cached,
 * so search and reading work with zero network.
 *
 * The heavy sqlite-wasm module is lazy-imported on first search so it never
 * weighs on app startup — it shares the same CaselawDB used by the opinions tab.
 */
import type { RuleHit } from '@/data/caselaw-db';
import { getCaselawDb as getDb } from '@/data/caselaw-db-instance';
import { byId } from '@/ui/dom';
import { showModal } from '@/ui/overlays';
import { escapeHTML } from '@/utils/formatters';
import { linkifyToHtml, citedByHtml } from '@/features/law-cite';

const VERIFY_NOTE =
  'Verbatim rule text published by the Massachusetts Trial Court Law Libraries ' +
  '(mass.gov). This is not an official copy of the rules — verify the current ' +
  'text and any amendments against the official source before relying on it.';

// The Guide to Evidence comes from a different publisher (the SJC's Advisory
// Committee, not the Trial Court Law Libraries) and, unlike the procedural rule
// sets, is expressly NOT adopted rules — attributing it to the wrong body, or
// implying it binds like a rule, would both be wrong in a tool used in court.
const GUIDE_VERIFY_NOTE =
  'Published by the Supreme Judicial Court’s Advisory Committee on Massachusetts ' +
  'Evidence Law (mass.gov). The Guide restates existing Massachusetts evidence law ' +
  'and is expressly NOT a set of adopted rules of evidence (see § 102) — cite the ' +
  'underlying authority it collects, and verify against the current edition.';

/** Provenance note appropriate to the rule set a hit belongs to. */
function verifyNoteFor(ruleSet: string | null): string {
  return /Guide to Evidence/i.test(ruleSet || '') ? GUIDE_VERIFY_NOTE : VERIFY_NOTE;
}

/** Run a full-text search over the offline court rules and render the results. */
export function searchRules(): void {
  const input = byId<HTMLInputElement>('law-rule-search');
  const results = byId('law-rule-results');
  if (!results) return;
  const q = input ? input.value.trim() : '';
  if (!q) { results.innerHTML = ''; return; }
  results.innerHTML =
    '<div style="font-size:12px;color:var(--text-muted);padding:8px 2px;">Searching the offline court-rules library…</div>';
  void (async () => {
    const db = await getDb();
    if (!db) {
      results.innerHTML =
        '<div style="font-size:12px;color:#b45309;padding:8px 2px;">Offline rules database unavailable on this device.</div>';
      return;
    }
    let hits: RuleHit[] = [];
    try {
      hits = db.searchRules(q, 40);
    } catch {
      hits = [];
    }
    if (!hits.length) {
      results.innerHTML =
        `<div style="font-size:12px;color:var(--text-muted);padding:8px 2px;">No court rules matched “${escapeHTML(q)}”.</div>`;
      return;
    }
    let html =
      `<div style="font-size:10px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;margin:4px 2px 6px;">${hits.length} rule${hits.length === 1 ? '' : 's'}</div>`;
    for (const h of hits) {
      const label = [h.ruleNumber, h.title].filter(Boolean).map((s) => escapeHTML(String(s))).join(' — ');
      const meta = [h.ruleSet, h.effective ? `eff. ${h.effective}` : '']
        .filter(Boolean)
        .map((s) => escapeHTML(String(s)))
        .join(' · ');
      html +=
        `<button onclick="openRule('${escapeHTML(h.docId)}')" style="display:block;width:100%;text-align:left;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:9px 11px;margin-bottom:6px;cursor:pointer;">` +
        `<div style="font-size:13px;font-weight:600;color:#1e293b;">${label || escapeHTML(h.docId)}</div>` +
        (meta ? `<div style="font-size:11px;color:#64748b;margin-top:2px;">${meta}</div>` : '') +
        '</button>';
    }
    results.innerHTML = html;
  })();
}

/** Open one rule's verbatim full text in a modal. */
export function openRule(docId: string): void {
  void (async () => {
    const db = await getDb();
    if (!db) return;
    const hit = db.getRule(docId);
    showModal('<div style="padding:20px;color:var(--text-muted);font-size:13px;">Loading rule…</div>');
    let text: string | null = null;
    try {
      text = await db.getRuleText(docId);
    } catch {
      text = null;
    }
    const heading = hit
      ? escapeHTML([hit.ruleNumber, hit.title].filter(Boolean).join(' — '))
      : escapeHTML(docId);
    const ruleSet = hit && hit.ruleSet ? escapeHTML(hit.ruleSet) : '';
    const sub = [hit?.court, hit?.effective ? `effective ${hit.effective}` : '']
      .filter(Boolean)
      .map((s) => escapeHTML(String(s)))
      .join(' · ');
    const source =
      hit && hit.url
        ? `<a href="${escapeHTML(hit.url)}" target="_blank" rel="noopener" style="color:#2563eb;text-decoration:none;font-weight:600;">↗ Source: ${escapeHTML(hit.source || 'mass.gov')}</a>`
        : `Source: ${escapeHTML(hit?.source || 'mass.gov')}`;
    const bodyHtml = text
      ? await linkifyToHtml(text)
      : '<em>Full text is not available offline for this rule.</em>';
    const citedBy = await citedByHtml(docId);
    showModal(
      `<div style="font-family:Georgia,serif;font-weight:600;font-size:16px;color:#1e293b;margin-bottom:2px;">${heading}</div>` +
        (ruleSet ? `<div style="font-size:12px;color:#64748b;">${ruleSet}</div>` : '') +
        (sub ? `<div style="font-size:11px;color:#94a3b8;margin-top:1px;">${sub}</div>` : '') +
        `<div style="font-size:11px;margin:8px 0 10px;">${source}</div>` +
        `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:9px 11px;margin-bottom:12px;font-size:11px;color:#78350f;line-height:1.6;">⚠ ${verifyNoteFor(hit?.ruleSet ?? null)}</div>` +
        citedBy +
        `<div style="max-height:60vh;overflow:auto;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-family:Georgia,serif;font-size:13px;line-height:1.75;color:#1e293b;">${bodyHtml}</div>`
    );
  })();
}
